import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const DEFAULT_SECTIONS = {
  milk: true,
  statements: true,
  docs: true,
  compare: true,
  forecast: true,
  forms: true
};

const SECTION_LABELS = {
  milk: 'Milk',
  statements: 'Statements',
  docs: 'Docs',
  compare: 'Compare',
  forecast: 'Forecast',
  forms: 'Forms'
};

const normalizeSections = (sections = {}) => {
  const normalized = { ...sections };
  if (Object.prototype.hasOwnProperty.call(normalized, 'charts')) {
    if (!Object.prototype.hasOwnProperty.call(normalized, 'compare')) {
      normalized.compare = normalized.charts;
    }
    delete normalized.charts;
  }
  return normalized;
};


const labColumns = [
  'Fat',
  'Protein',
  'Lactose',
  'CellCount'
  // TODO: put your real DB column names here
];

const statusDotColor = (status) => {
  switch (status) {
    case 'active':
      return '#22c55e'; // green
    case 'inactive':
      return '#9ca3af'; // grey
    default:
      return '#9ca3af';
  }
};


const Instances = () => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingInstance, setEditingInstance] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    status: 'active',
    brand_logo: null,
    brand_color: '#0f3d9a',
    secondary_color: '#4f6bd7',
    active_text_color: 'dark',
    sections: { ...DEFAULT_SECTIONS },
    language: 'English',
    company_code: ''
  });

  useEffect(() => {
    fetchInstances();
  }, []);

  const fetchInstances = async () => {
    try {
      const token = localStorage.getItem('udsAdminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASEURL}/api/instances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const mapped = response.data.map((instance) => ({
        ...instance,
        company_code: instance.company_code ? instance.company_code.trim() : ''
      }));
      setInstances(mapped);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch instances');
      setLoading(false);
    }
  };

  const loadTenantUsers = async (instanceId) => {
    try {
      const token = localStorage.getItem('udsAdminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASEURL}/api/users?scope=tenant&instance_id=${instanceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentTenantUsers(response.data);
    } catch (err) {
      console.error('Failed to load tenant users:', err);
      setCurrentTenantUsers([]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      status: 'active',
      brand_logo: null,
      brand_color: '#0f3d9a',
      secondary_color: '#4f6bd7',
      active_text_color: 'dark',
      sections: { ...DEFAULT_SECTIONS },
      language: 'English',
      company_code: ''
    });
    setShowCreateForm(false);
    setEditingInstance(null);
    setError('');
    setCurrentTenantUsers([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const trimmedCompanyCode = (formData.company_code || '').trim();

      if (!trimmedCompanyCode) {
        setError('Company Code is required');
        setSubmitting(false);
        return;
      }

      if (trimmedCompanyCode.length > 14) {
        setError('Company Code must be 14 characters or less');
        setSubmitting(false);
        return;
      }

      const token = localStorage.getItem('udsAdminToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      };

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('status', formData.status);
      if (formData.brand_logo) {
        formDataToSend.append('brand_logo', formData.brand_logo);
      }
      formDataToSend.append('brand_color', formData.brand_color);
      formDataToSend.append('secondary_color', formData.secondary_color);
      formDataToSend.append('active_text_color', formData.active_text_color);
      formDataToSend.append('sections', JSON.stringify(normalizeSections(formData.sections)));
      formDataToSend.append('language', formData.language);
      formDataToSend.append('company_code', trimmedCompanyCode);

      if (editingInstance) {
        await axios.put(
          `${process.env.REACT_APP_API_BASEURL}/api/instances/${editingInstance.instance_id}`,
          formDataToSend,
          config
        );
        resetForm();
        fetchInstances();
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASEURL}/api/instances`,
          formDataToSend,
          config
        );

        const createdInstance = {
          instance_id: response.data.instance_id,
          name: formData.name,
          company_code: trimmedCompanyCode
        };

        window.dispatchEvent(new CustomEvent('instanceCreated', { detail: createdInstance }));

        resetForm();
        fetchInstances();
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save instance';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (instance) => {
    setEditingInstance(instance);
    const parsedSections = instance.sections
      ? normalizeSections(JSON.parse(instance.sections))
      : {};
    const mergedSections = { ...DEFAULT_SECTIONS, ...parsedSections };

    setFormData({
      name: instance.name,
      status: instance.status,
      brand_logo: null,
      brand_color: instance.brand_color || '#0f3d9a',
      secondary_color: instance.secondary_color || '#4f6bd7',
      active_text_color: instance.active_text_color || 'dark',
      sections: mergedSections,
      language: instance.language || 'English',
      company_code: instance.company_code ? instance.company_code.trim() : ''
    });
    setShowCreateForm(true);

    await loadTenantUsers(instance.instance_id);
  };

  const handleDelete = async (instanceId) => {
    if (!window.confirm('Delete this instance? This cannot be undone.')) return;

    try {
      const token = localStorage.getItem('udsAdminToken');
      await axios.delete(`${process.env.REACT_APP_API_BASEURL}/api/instances/${instanceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInstances();
    } catch (err) {
      setError('Failed to delete instance');
    }
  };

  const handleSectionChange = (section) => {
    const updatedSections = {
      ...formData.sections,
      [section]: !formData.sections[section]
    };
  
    const normalizedSections = normalizeSections(updatedSections);
  
    setFormData((prev) => ({
      ...prev,
      sections: normalizedSections
    }));
  };
  

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  const [currentTenantUsers, setCurrentTenantUsers] = useState([]);

  const brandLogoPreview = useMemo(() => {
    if (formData.brand_logo) {
      return URL.createObjectURL(formData.brand_logo);
    }
    return null;
  }, [formData.brand_logo]);

  useEffect(() => {
    return () => {
      if (brandLogoPreview) {
        URL.revokeObjectURL(brandLogoPreview);
      }
    };
  }, [brandLogoPreview]);

  const existingLogoUrl =
    editingInstance && editingInstance.brand_logo
      ? `${process.env.REACT_APP_API_BASEURL}/uploads/${editingInstance.brand_logo}`
      : '';

  if (loading) {
    return (
      <div className="page-shell">
        <div className="card">
          <p>Loading instances…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Instances</h1>
          <p className="page-subtitle">
            Provision tenant environments and tie them to the correct company codes. Instance IDs are assigned automatically when you save.
          </p>
        </div>
        {!showCreateForm && (
          <button className="button" onClick={() => setShowCreateForm(true)}>
            <i className="uicon-add" /> <span>Create Instance</span>
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}

      {showCreateForm ? (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">{editingInstance ? 'Edit Instance' : 'Create Instance'}</h2>
            <button type="button" className="button ghost sm" onClick={resetForm}>
              <i className="uicon-arrow-narrow-left" /> Back to list
            </button>
          </div>

          <form onSubmit={handleSubmit} className="form-stack">
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="instance-name">Name</label>
                <div className="form-field-control">
                  <input
                    type="text"
                    id="instance-name"
                    placeholder="Instance Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="instance-company-code">Company Code</label>
                <div className="form-field-control">
                  <input
                    type="text"
                    id="instance-company-code"
                    placeholder="Company Code (max 14 characters)"
                    value={formData.company_code}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Limit to 14 characters
                      if (value.length <= 14) {
                        setFormData({ ...formData, company_code: value });
                      }
                    }}
                    maxLength={14}
                    required
                  />
                  <div style={{ fontSize: '12px', color: '#6b7a90', marginTop: '4px' }}>
                    {formData.company_code.length}/14 characters
                  </div>
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="instance-status">Status</label>
                <div className="form-field-control form-field-control-select">
                  <select
                    id="instance-status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="instance-logo">Brand Logo</label>
                <div className="form-field-control form-field-file">
                  <span
                    className="form-field-file-preview"
                    style={{
                      backgroundImage: brandLogoPreview
                        ? `url(${brandLogoPreview})`
                        : existingLogoUrl
                        ? `url(${existingLogoUrl})`
                        : '',
                      backgroundColor:
                        formData.brand_logo || existingLogoUrl ? 'transparent' : formData.brand_color
                    }}
                  />
                  <label className="form-field-file-label" htmlFor="instance-logo">
                    <i className="uicon-export" />
                    <span>Upload logo</span>
                    <input
                      type="file"
                      accept="image/*"
                      id="instance-logo"
                      onChange={(e) => setFormData({ ...formData, brand_logo: e.target.files[0] })}
                    />
                  </label>
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="instance-color">Brand Color</label>
                <div className="form-field-control">
                  <input
                    type="color"
                    id="instance-color"
                    value={formData.brand_color}
                    onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="secondary-color">Secondary Color</label>
                <div className="form-field-control">
                  <input
                    type="color"
                    id="secondary-color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="active-text-color">Active Text Color</label>
                <div className="form-field-control form-field-control-select">
                  <select
                    id="active-text-color"
                    value={formData.active_text_color}
                    onChange={(e) => setFormData({ ...formData, active_text_color: e.target.value })}
                  >
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                  </select>
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="instance-language">Language</label>
                <div className="form-field-control form-field-control-select">
                  <select
                    id="instance-language"
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  >
                    <option value="English">English</option>
                    <option value="German">German</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="card-subsection">
              <h3>Feature Access</h3>
              <div className="feature-grid">
                {Object.keys(formData.sections).map((sectionKey) => (
                  <label key={sectionKey} className="feature-toggle">
                    <input
                      type="checkbox"
                      checked={formData.sections[sectionKey]}
                      onChange={() => handleSectionChange(sectionKey)}
                    />
                    <span>{SECTION_LABELS[sectionKey] || sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)}</span>
                  </label>
                ))}
              </div>
            </div>

            {editingInstance && (
              <div className="card-subsection">
                <div className="card-header" style={{ marginBottom: 0 }}>
                  <h3 className="card-title" style={{ margin: 0 }}>Linked Users</h3>
                </div>
                <div style={{ marginTop: '16px' }}>
                  {currentTenantUsers.length > 0 ? (
                    <table className="uds-table">
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th>Username</th>
                          <th>Owner</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTenantUsers.map((user) => (
                          <tr key={`${user.supplier}-${user.username}`}>
                            <td>{user.supplier}</td>
                            <td>{user.username}</td>
                            <td>{user.owner}</td>
                            <td>{user.type}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: '12px 0', color: '#6b7a90' }}>
                      No users linked to this instance yet.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="button" disabled={submitting}>
                {submitting ? 'Saving…' : editingInstance ? 'Update Instance' : 'Create Instance'}
              </button>
              <button type="button" className="button secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="card table-card">
          <table className="uds-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Company</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {instances.map((instance) => (
                <tr key={instance.instance_id}>
                  <td>{instance.instance_id}</td>
                  <td>{instance.name}</td>
                  <td>{instance.company_code || '-'}</td>
                  <td>
                    <span className="status-badge">
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: statusDotColor(instance.status),
                          display: 'inline-block'
                        }}
                      />
                      {instance.status}
                    </span>
                  </td>
                  <td>{formatDate(instance.created_date)}</td>
                  <td className="table-actions">
                    <button
                      type="button"
                      className="action-button"
                      data-type="edit"
                      onClick={() => handleEdit(instance)}
                      title="Edit instance"
                    >
                      <i className="uicon-edit" />
                    </button>
                    <button
                      type="button"
                      className="action-button"
                      data-type="trash"
                      onClick={() => handleDelete(instance.instance_id)}
                      title="Delete instance"
                    >
                      <i className="uicon-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Instances;
